import type { Client2TrackedTranslation, Values } from '@ps/web-core-intl';
import { select } from '@ps/web-core-store';
import type { ButtonProps as FusionButtonProps } from '@ps/web-fusion-components';
import { Button as FusionButton } from '@ps/web-fusion-components';
import { useTrackers } from '@ps/web-shared-analytics';
import type { ButtonHTMLAttributes, DOMAttributes, ReactNode } from 'react';
import type React from 'react';
import { useRef } from 'react';
import type { PressEvent } from 'react-aria';
import { useSelector } from 'react-redux';

import Translation from '../../Translation/Translation';

export type Props = Omit<Partial<DOMAttributes<HTMLButtonElement>>, 'onClick'> &
  Omit<ButtonHTMLAttributes<HTMLButtonElement>, 'onClick' | 'disabled'> &
  FusionButtonProps & {
    title?: ReactNode | Element;
    routeValues?: { [key: string]: string };
    translation?: Client2TrackedTranslation;
    values?: Values;
    trackText?: string;
    track?: boolean;
    trackingValue?: number;
    trackingDimensions?: Record<string, string | number> | undefined;
  };

const Button: React.FC<Props> = ({
  size = 'large',
  type = 'button',
  variant = 'primary',
  translation,
  values,
  routeValues = {},
  trackingValue,
  trackingDimensions,
  track = true,
  ...props
}) => {
  const locale = useSelector(select.locale.get);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const { buttonPressed } = useTrackers();

  const children = translation ? <Translation {...translation} values={values} /> : props.children;

  const handlePress = (event: PressEvent) => {
    const { onPress } = props;
    const trackData =
      props.trackText || (locale === 'en' ? buttonRef.current?.innerText : undefined);

    onPress && onPress(event);
    track && buttonPressed({ buttonName: trackData, value: trackingValue }, trackingDimensions);
  };

  return (
    <FusionButton
      ref={buttonRef}
      type={type}
      size={size}
      variant={variant}
      onPress={handlePress}
      {...props}
    >
      {children}
    </FusionButton>
  );
};

export default Button;
